import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "./lib/api";
import Layout from "./components/Layout";
import AuthCallback from "./components/AuthCallback";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import EmployeeWizard from "./pages/EmployeeWizard";
import Attendance from "./pages/Attendance";
import SettingsPage from "./pages/Settings";
import Kiosk from "./pages/Kiosk";
import Incidents from "./pages/Incidents";
import Payroll from "./pages/Payroll";
import PayrollAdjustments from "./pages/PayrollAdjustments";
import Vales from "./pages/Vales";
import Contracts from "./pages/Contracts";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);

  // Google OAuth callback se maneja en la ruta /auth/google.
  // Si llegamos aquí desde el callback (con ?code en URL), no hace falta auth-check.
  const isGoogleCallback = location.pathname === "/auth/google";

  const checkAuth = useCallback(async () => {
    if (window.location.pathname === "/auth/google") {
      setChecking(false);
      return;
    }
    const token = localStorage.getItem("vetcomer_token");
    if (!token) {
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("vetcomer_token");
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!user) checkAuth();
    else setChecking(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuth = (userData) => {
    setUser(userData);
    setChecking(false);
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    localStorage.removeItem("vetcomer_token");
    setUser(null);
    navigate("/login", { replace: true });
  };

  // Google OAuth callback — siempre permitir aunque no haya user
  if (isGoogleCallback) {
    return <AuthCallback onAuth={handleAuth} />;
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/kiosko" element={<Kiosk />} />
      <Route
        path="/login"
        element={
          user ? <Navigate to="/" replace /> : <Login onDevLogin={handleAuth} />
        }
      />
      <Route
        element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/empleados" element={<Employees />} />
        <Route path="/empleados/nuevo" element={<EmployeeWizard />} />
        <Route path="/empleados/:id" element={<EmployeeDetail />} />
        <Route path="/asistencia" element={<Attendance />} />
        <Route path="/incidencias" element={<Incidents />} />
        <Route path="/nomina" element={<Payroll />} />
        <Route path="/nomina/reconciliacion" element={<PayrollAdjustments />} />
        <Route path="/vales" element={<Vales />} />
        <Route path="/contratos" element={<Contracts />} />
        <Route path="/reportes" element={<Reports />} />
        <Route path="/notificaciones" element={<Notifications />} />
        <Route path="/configuracion" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
